import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from 'react-daisyui';
import Toggle from '@/components/shared/Toggle/Toggle.tsx';
import ChangeActivityModal from '@/components/modals/ChangeActivityModal/ChangeActivityModal.tsx';
import { allDayQuickChanges } from '$/_const/trip/ChangeActivityReason.ts';
import { useTripContext } from '@/context/TripContext.tsx';
import refineDayErrorKey from '@/utils/trip/refineDayErrorKey.ts';
import { pillClassName } from '@/components/shared/tripControlClassNames.ts';

const DayChangeButtons: FC<{ day: number }> = ({ day }) => {
  const { t } = useTranslation();
  const { isLoadingAction, loadingDays, refineDayErrors, handleRefineDay } = useTripContext();
  const labelId = `day-${day}-changes`;
  const dayError = refineDayErrors.find((error) => error.day === day);
  const errorRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (dayError) {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [dayError]);

  return (
    <div className="mt-8" role="group" aria-labelledby={labelId}>
      <p id={labelId} className="font-sans text-sm font-medium text-[#2F2D2A]">
        {t('tripPage.change_this_day')}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Toggle
          renderTrigger={({ toggle }) => (
            <button type="button" className={pillClassName} disabled={isLoadingAction} onClick={toggle}>
              {t('button.swap_experience')}
            </button>
          )}
          renderContent={({ isOpen, handleClose }) => (
            <ChangeActivityModal isOpen={isOpen} onClose={handleClose} dayToRefine={day} />
          )}
        />
        {allDayQuickChanges.map((reason) => (
          <button
            key={reason}
            type="button"
            className={pillClassName}
            disabled={isLoadingAction}
            onClick={() => handleRefineDay(day, { reason })}
          >
            {t(`button.${reason}`)}
          </button>
        ))}
      </div>
      {dayError ? (
        <div ref={errorRef} className="mt-3">
          {dayError.message ? (
            <p
              role="status"
              className="text-pretty rounded-[14px] border border-primary/60 bg-primary/10 px-4 py-3 text-sm text-[#2F2D2A]"
            >
              {dayError.message}
            </p>
          ) : (
            <Alert status="error" role="alert">
              {t(refineDayErrorKey(dayError.code, dayError.status))}
            </Alert>
          )}
        </div>
      ) : (
        <p aria-live="polite" className="mt-3 min-h-5 text-sm text-text-secondary">
          {loadingDays.includes(day) ? t('tripPage.reworking_day', { number: day }) : ''}
        </p>
      )}
    </div>
  );
};

export default DayChangeButtons;
